import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
console.log('🔧 API_BASE_URL:', API_BASE_URL);
console.log('🔧 Environment check:', process.env.NODE_ENV);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname || '';
      if (path.startsWith('/invite')) {
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => {
    console.log('API: Attempting login with:', email);
    return api.post('/auth/login', { email, password })
      .then(res => {
        console.log('API: Login response:', res.data);
        return res.data;
      })
      .catch(error => {
        console.error('API: Login error:', error);
        throw error;
      });
  },
  
  register: (email, username, password) => {
    return api.post('/auth/register', { email, username, password }).then(res => res.data);
  },
  
  getCurrentUser: () => {
    return api.get('/auth/me').then(res => res.data);
  },

  previewSeatInvite: (token) => {
    return api.get(`/auth/seat-invite/${encodeURIComponent(token)}`).then((res) => res.data);
  },

  acceptSeatInvite: (token, password) => {
    return api
      .post(`/auth/seat-invite/${encodeURIComponent(token)}`, { password })
      .then((res) => res.data);
  },
};

  // Gaming API - Updated to use VM gaming endpoints
  export const gamingAPI = {
    getSessions: () => {
      return api.get('/vm-gaming/sessions').then(res => res.data);
    },
  
  createSession: (sessionData) => {
    // Game = VM Wolf; Lab = Docker (future AI)
    return api.post('/vm-gaming/create-vm', {
      gpu_type: sessionData.gpu_type,
      max_price_per_hour: sessionData.max_price_per_hour,
      session_name: sessionData.session_name,
      game_title: sessionData.game_title || '',
      disk_space: sessionData.instance_mode === 'lab' ? 50 : Math.round(Number(sessionData.disk_space) || 0),
      keep_gaming_data: true,
      gpu_id: sessionData.gpu_id,
      instance_mode: sessionData.instance_mode || 'game',
      play_backend: sessionData.play_backend || '',
      quoted_usd_per_hour: Number(sessionData.quoted_usd_per_hour) || 0,
      gpu_ram: Number(sessionData.gpu_ram) || 0,
    }, { timeout: 180000 }).then(res => res.data);
  },
  
  getSessionStatus: (sessionId) => {
    return api.get(`/vm-gaming/vm-status/${sessionId}`).then(res => res.data);
  },
  
  getSessionDetails: (sessionId) => {
    return api.get(`/vm-gaming/vm-status/${sessionId}`).then(res => res.data);
  },
  
  getVMTemplates: () => {
    return api.get('/vm-gaming/vm-templates').then(res => res.data);
  },
  
  // VM Management API
  getVMStatus: (instanceId) => {
    return api.get(`/vm-management/status/${instanceId}`).then(res => res.data);
  },
  
  getVMLogs: (instanceId) => {
    return api.get(`/vm-management/logs/${instanceId}`).then(res => res.data);
  },
  
  stopVM: (instanceId) => {
    return api.post(`/vm-management/stop/${instanceId}`).then(res => res.data);
  },
  
  getMoonlightProfile: (instanceId) => {
    return api.get(`/vm-management/moonlight-profile/${instanceId}`).then(res => res.data);
  },
  
  stopSession: (instanceId) => {
    // Destroys the Vast.ai instance (stops billing). Returns as soon as
    // destroy is queued — Steam copy runs in the background.
    return api.post(`/vm-gaming/sessions/${instanceId}/stop`, {}, { timeout: 25000 }).then(res => res.data);
  },

  destroySession: (instanceId) => {
    return api.post(`/vm-gaming/sessions/${instanceId}/stop`).then(res => res.data);
  },
  
  cleanupOrphaned: () => {
    return api.post('/vm-gaming/sessions/cleanup').then(res => res.data);
  },
  
  createTunnel: (sessionId) => {
    return api.post(`/gaming/sessions/${sessionId}/tunnel`).then(res => res.data);
  },
  
  closeTunnel: (sessionId) => {
    return api.delete(`/gaming/sessions/${sessionId}/tunnel`).then(res => res.data);
  },
  
  // User preferences
  getPreferences: () => {
    return api.get('/preferences/preferences').then(res => res.data);
  },
  
  updatePreferences: (preferencesData) => {
    return api.put('/preferences/preferences', preferencesData).then(res => res.data);
  },
  
  // Volume management
  getVolumeStatus: (userId) => {
    return api.get(`/gaming/volumes/${userId}/status`).then(res => res.data);
  },
  
  // Session cleanup
  cleanupOrphanedSessions: () => {
    return api.post('/gaming/cleanup-orphaned-sessions').then(res => res.data);
  },
  
  // Wolf Pin Link API - Use instance_id for VM gaming sessions
  getSessionPinLink: (instanceId) => {
    // The gaming endpoint accepts instance_id and checks vast_instance_id
    return api.get(`/gaming/sessions/${instanceId}/pin-link`).then(res => res.data);
  },

  getPlayConfig: () => {
    return api.get('/vm-gaming/play-config').then(res => res.data);
  },

  getBrowserPlay: (instanceId, view = 'wolf') => {
    return api.get(`/vm-gaming/sessions/${instanceId}/browser-play`, { params: { view } }).then(res => res.data);
  },

  playHeartbeat: (sessionDbId) => {
    return api
      .post(`/vm-gaming/sessions/${sessionDbId}/play-heartbeat`, { active: true })
      .catch(() => {});
  },

  getPlayConnect: (instanceId, view = 'wolf') => {
    return api
      .get(`/vm-gaming/sessions/${instanceId}/play-connect`, {
        params: { view },
        timeout: 8000,
      })
      .then((res) => res.data);
  },

  getBootProgress: (sessionId) => {
    return api
      .get(`/vm-gaming/sessions/${sessionId}/boot-progress`, { timeout: 12000 })
      .then((res) => res.data);
  },
  
  getWolfPinLink: () => {
    return api.get('/gaming/wolf/pin-link').then(res => res.data);
  },
  
  verifyPin: (sessionId, pinCode) => {
    return api.post(`/gaming/sessions/${sessionId}/verify-pin?pin_code=${encodeURIComponent(pinCode)}`).then(res => res.data);
  },
  
  submitPin: (sessionId, pinCode) => {
    return api.post(`/gaming/sessions/${sessionId}/submit-pin?pin_code=${encodeURIComponent(pinCode)}`).then(res => res.data);
  },
};

// Parsec API
export const parsecAPI = {
  createUser: (userData) => {
    return api.post('/parsec/create-user', userData).then(res => res.data);
  },
  
  getConnectionInfo: () => {
    return api.get('/parsec/connection-info').then(res => res.data);
  },
  
  setupContainer: (containerId) => {
    return api.post(`/parsec/setup-container/${containerId}`).then(res => res.data);
  },
  
  verifyConnection: (containerId) => {
    return api.get(`/parsec/verify-connection/${containerId}`).then(res => res.data);
  },
};

// Exchange Rate API
export const exchangeRateAPI = {
  getRate: () => {
    return api.get('/exchange-rates/rate').then(res => res.data);
  },
  
  convertUsdToMkd: (usdAmount) => {
    return api.get(`/exchange-rates/convert?usd_amount=${usdAmount}`).then(res => res.data);
  },
};

// GPU API
export const gpuAPI = {
  // opts: { region, userRegion, sortBy, minInetDownMbps, verifyIp, probeRtt, hideMismatch }
  //   region        - "EU" | "NA" | "NA-EAST" | "NA-WEST" | "ASIA" | "SA" | "OCEANIA" | "ME" | "any"
  //   userRegion    - anchor used server-side to estimate RTT (defaults to EU)
  //   sortBy        - "price" | "latency" | "value" | "location"
  //   verifyIp      - Level 2: cross-check public IP country vs listed geolocation
  //   probeRtt      - Level 4: fire backend TCP-handshake probe for a real RTT
  //   hideMismatch  - drop hosts whose IP resolves to a different country
  getAvailableGPUs: (gpuType, instanceMode = 'lab', opts = {}) => {
    const {
      region, userRegion, sortBy, minInetDownMbps,
      verifyIp, probeRtt, hideMismatch, familyQuad,
    } = opts;
    const params = {
      ...(gpuType ? { gpu_type: gpuType } : {}),
      instance_mode: instanceMode,
      ...(region ? { region } : {}),
      ...(userRegion ? { user_region: userRegion } : {}),
      ...(sortBy ? { sort_by: sortBy } : {}),
      ...(minInetDownMbps ? { min_inet_down_mbps: minInetDownMbps } : {}),
      ...(verifyIp === false ? { verify_ip: false } : {}),
      ...(probeRtt === false ? { probe_rtt: false } : {}),
      ...(hideMismatch ? { hide_mismatch: true } : {}),
      ...(familyQuad ? { family_quad: true } : {}),
    };
    return api.get('/gpu/available', { params }).then(res => res.data);
  },
  
  getGPUPricing: () => {
    return api.get('/gpu/pricing').then(res => res.data);
  },
  
  getGPURecommendations: (budget) => {
    return api.get('/gpu/recommendations', { params: { budget_per_hour: budget } }).then(res => res.data);
  },
};

// Payments API
export const paymentsAPI = {
  getSubscriptions: () => {
    return api.get('/payments/subscriptions').then(res => res.data);
  },
  
  createSubscription: (subscriptionData) => {
    return api.post('/payments/subscriptions', subscriptionData).then(res => res.data);
  },
  
  getPayments: () => {
    return api.get('/payments/payments').then(res => res.data);
  },
  
  getCashback: () => {
    return api.get('/payments/cashback').then(res => res.data);
  },

  getLemonStatus: () => {
    return api.get('/payments/lemon/status').then(res => res.data);
  },

  createLemonCheckout: (amountMkd) => {
    return api.post('/payments/lemon/checkout', { amount_mkd: amountMkd }).then(res => res.data);
  },

  getLemonSubscription: () => {
    return api.get('/payments/lemon/subscription').then(res => res.data);
  },

  subscribe: (sku = 'solo') => {
    return api.post('/payments/lemon/subscribe', { sku }).then(res => res.data);
  },
  
  topUpWallet: (amount) => {
    return api.post('/payments/lemon/checkout', { amount_mkd: amount }).then(res => res.data);
  },
};

export const familyAPI = {
  seats: () => api.get('/family/seats').then((res) => res.data),
  mint: () => api.post('/family/mint').then((res) => res.data),
  invite: (seat) => api.post(`/family/seats/${seat}/invite`).then((res) => res.data),
};

export const wireguardAPI = {
  mine: () => api.get('/wireguard/mine').then((res) => res.data),
};

export const partyAPI = {
  mine: () => api.get('/party-lan/mine').then((res) => res.data),
  create: (body) => api.post('/party-lan', body).then((res) => res.data),
  rsvpYes: (id) => api.post(`/party-lan/${id}/rsvp-yes`).then((res) => res.data),
  rsvpNo: (id) => api.post(`/party-lan/${id}/rsvp-no`).then((res) => res.data),
  checkIn: (id) => api.post(`/party-lan/${id}/check-in`).then((res) => res.data),
  cancel: (id) => api.post(`/party-lan/${id}/cancel`).then((res) => res.data),
};

export const adminAPI = {
  getOverview: () => {
    return api.get('/admin/overview').then(res => res.data);
  },
  getUsage: () => {
    return api.get('/admin/usage').then(res => res.data);
  },
  patchCaps: (caps) => {
    return api.patch('/admin/caps', caps).then(res => res.data);
  },
  // Phase A: per-user NAS iSCSI library
  iscsiUsers: () => api.get('/admin/iscsi/users').then(res => res.data),
  iscsiToggle: (body) => api.post('/admin/iscsi/toggle', body).then(res => res.data),
  iscsiMigrateHousehold: (sourceUserId) =>
    api.post('/admin/iscsi/migrate-household', { source_user_id: sourceUserId }).then(res => res.data),
  iscsiBulkProvision: () => api.post('/admin/iscsi/bulk-provision').then(res => res.data),
};

export const lobbyAPI = {
  mine: () => api.get('/play/lobbies/mine').then((res) => res.data),
  usage: () => api.get('/play/lobbies/usage').then((res) => res.data),
  create: (body) => api.post('/play/lobbies', body).then((res) => res.data),
  vote: (id, startWith) =>
    api.post(`/play/lobbies/${id}/vote`, { start_with: startWith }).then((res) => res.data),
  leave: (id) => api.post(`/play/lobbies/${id}/leave`).then((res) => res.data),
};

// Users API
export const usersAPI = {
  getUserStats: () => {
    return api.get('/users/stats').then(res => res.data);
  },
  
  updateProfile: (profileData) => {
    return api.put('/users/profile', profileData).then(res => res.data);
  },

  // Phase C — self-service NAS Storage from Profile.
  getMyStorage: () => api.get('/users/storage').then(res => res.data),
  enableMyStorage: (sizeGb) =>
    api.post('/users/storage', { size_gb: Number(sizeGb) }).then(res => res.data),
  removeMyStorage: () => api.post('/users/storage/remove').then(res => res.data),
};

export const vmMonitoringAPI = {
  getVMStatus: (instanceId) => api.get(`/vm-monitoring/status/${instanceId}`),
  getServiceHealth: (instanceId) => api.get(`/vm-monitoring/health/${instanceId}`),
  getConnectionInstructions: (instanceId) => api.get(`/vm-monitoring/instructions/${instanceId}`),
  autoTerminate: () => api.post('/vm-monitoring/auto-terminate'),
  getMonitoringSummary: () => api.get('/vm-monitoring/monitoring/summary')
};

export const parsecUsersAPI = {
  createUser: () => api.post('/parsec-users/create-user'),
  getUserStatus: () => api.get('/parsec-users/user-status'),
  getConnectionInfo: (instanceId) => api.get(`/parsec-users/connection-info/${instanceId}`),
  verifyInstallation: (instanceId) => api.get(`/parsec-users/verify-installation/${instanceId}`)
};

export default api;
